import type { FragmentType } from 'src/gql';
import { Building2, ChartPie, House, Palette, Shapes, Shield, Truck } from 'lucide-react';
import { graphql, unmaskFragment } from 'src/gql';

import type { AssetClassName } from '@savvy/constants/generated/gql-enums';
import { financialAccountIconColors } from '@savvy/deprecated-shared/theme';
import defaultGuard from '@savvy/utils/default-guard';

import { formatItemValue, renderItemIcon } from './portfolio-item-display';

export const RenderIconForAssetFragment = graphql(/* GraphQL */ `
  fragment RenderIconForAsset on Asset {
    assetClass
    assetSubclass
  }
`);

export function renderIconForAsset(assetFrag: FragmentType<typeof RenderIconForAssetFragment>) {
  const asset = unmaskFragment(RenderIconForAssetFragment, assetFrag);
  switch (asset.assetClass) {
    case 'real_estate':
      if (asset.assetSubclass === 'non_residence') {
        return renderItemIcon(financialAccountIconColors.realEstateAsset, <Building2 color="#fff" size={20} />);
      }
      return renderItemIcon(financialAccountIconColors.realEstateAsset, <House color="#fff" size={20} />);
    case 'company_equity':
      return renderItemIcon(financialAccountIconColors.companyEquity, <ChartPie className="size-5 text-white" />);
    case 'vehicle':
      return renderItemIcon(financialAccountIconColors.otherAssets, <Truck color="#fff" size={20} />);
    case 'collectible':
      return renderItemIcon(financialAccountIconColors.otherAssets, <Shapes color="#fff" size={20} />);
    case 'insurance_policy':
      return renderItemIcon(financialAccountIconColors.otherAssets, <Shield color="#fff" size={20} />);
    case 'other':
      return renderItemIcon(financialAccountIconColors.otherAssets, <Palette color="#fff" size={20} />);
    default:
      return defaultGuard(
        asset.assetClass,
        renderItemIcon(financialAccountIconColors.otherAssets, <Palette color="#fff" size={20} />),
      );
  }
}

export function assetDeletionText(assetClass: AssetClassName) {
  let assetName1 = 'an asset';
  let assetName2 = 'asset';
  switch (assetClass) {
    case 'real_estate':
      assetName1 = 'a property';
      assetName2 = 'property';
      break;
    case 'company_equity':
      assetName1 = 'company equity';
      assetName2 = 'company equity';
      break;
    case 'collectible':
    case 'insurance_policy':
    case 'vehicle':
    case 'other':
      break;
    default:
      defaultGuard(assetClass);
      break;
  }
  return `When you delete ${assetName1} it will no longer appear in your list of accounts, assets, and liabilities. Historical values of this ${assetName2} will be removed from your net worth graph.`;
}

export function assetDeleteButtonText(assetClass: AssetClassName) {
  if (assetClass === 'company_equity') {
    return 'Delete company equity';
  } else if (assetClass === 'real_estate') {
    return 'Delete real estate';
  }
  return 'Delete asset';
}

export const AssetTitleFragment = graphql(/* GraphQL */ `
  fragment AssetTitle on Asset {
    assetClass
    companyEquityCompanyName
    description
  }
`);

export function assetTitle(assetFrag: FragmentType<typeof AssetTitleFragment>) {
  const asset = unmaskFragment(AssetTitleFragment, assetFrag);
  if (asset.assetClass === 'company_equity') {
    return asset.companyEquityCompanyName;
  }
  return asset.description;
}

export function assetValueTitle(asset: { value: { amount: string } }) {
  return formatItemValue(asset.value.amount);
}

export function assetValueSubtitle(asset: { liabilities: { currentBalance: { amount: string } }[] }) {
  if (asset.liabilities.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
    return `${formatItemValue(asset.liabilities[0]!.currentBalance.amount)} mortgage balance`;
  }
  return null;
}
